import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useCurrentCharacterAvatar } from "../hooks/useCurrentCharacterAvatar";
import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import { api } from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import {
  buildGroupTalkPreviewMembers,
  createParticipant,
  findGroupTalkMember,
  getGroupTalkAvailableStances,
  getGroupTalkDisplayMode,
  getGroupTalkFallbackImageSrc,
  groupTalkMemberOptions,
  isDuplicateParticipant,
  normalizeGroupTalkStanceId,
  type GroupTalkDraftMode,
  type GroupTalkMemberId,
  type GroupTalkParticipant,
  type GroupTalkStanceId,
} from "../lib/groupTalk";
import {
  clearGroupTalkDraft,
  loadGroupTalkDraft,
  saveGroupTalkActiveSessionSnapshot,
  saveGroupTalkDraft,
  type GroupTalkDraft,
} from "../lib/groupTalkSessionStorage";
import { buildCreateGroupTalkSessionParams, normalizeGroupTalkSessionView } from "../lib/groupTalkView";

type PreviewState = {
  draft?: GroupTalkDraft;
};

function resolveDraftMode(draft: GroupTalkDraft): GroupTalkDraftMode {
  return draft.mode ?? (draft.templateId === "custom" ? "custom" : "template");
}

function getFirstAvailableMember(participants: GroupTalkParticipant[]): GroupTalkMemberId {
  return groupTalkMemberOptions.find((member) =>
    member.id !== "mei" && !participants.some((participant) => participant.memberId === member.id)
  )?.id ?? "cmo";
}

function getDefaultStance(memberId: GroupTalkMemberId, mode: GroupTalkDraftMode): GroupTalkStanceId {
  return getGroupTalkAvailableStances(memberId, getGroupTalkDisplayMode(mode))[0]?.id ?? "bold";
}

export default function GroupTalkPreview() {
  const characterAvatarSrc = useCurrentCharacterAvatar();
  const characterName = useCurrentCharacterName();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as PreviewState;
  const loadedDraft = routeState.draft ?? loadGroupTalkDraft();
  const [draft, setDraft] = useState<GroupTalkDraft | null>(loadedDraft);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [addRoleId, setAddRoleId] = useState<GroupTalkMemberId>(() =>
    loadedDraft ? getFirstAvailableMember(loadedDraft.participants) : "cmo"
  );
  const mode = draft ? resolveDraftMode(draft) : "template";
  const displayMode = getGroupTalkDisplayMode(mode);
  const membersLocked = Boolean(draft?.memberEditingLocked || mode === "recommend");
  const canAddMembers = Boolean(draft && !membersLocked);

  const previewMembers = useMemo(() => {
    if (!draft) return [];
    return buildGroupTalkPreviewMembers(draft.participants, characterName, displayMode, characterAvatarSrc);
  }, [characterAvatarSrc, characterName, displayMode, draft]);

  const addableMembers = useMemo(() => {
    if (!draft) return [];
    return groupTalkMemberOptions.filter(
      (member) => member.id !== "mei" && !draft.participants.some((participant) => participant.memberId === member.id)
    );
  }, [draft]);

  const selectedAddMemberIsAvailable = addableMembers.some((member) => member.id === addRoleId);
  const addStanceId = getDefaultStance(addRoleId, mode);

  useEffect(() => {
    if (!draft) {
      navigate("/talk/group", {
        replace: true,
        state: { errorMessage: "会議設定が見つからなかったため、最初からやり直してください。" },
      });
    }
  }, [draft, navigate]);

  useEffect(() => {
    if (draft) {
      saveGroupTalkDraft(draft);
    }
  }, [draft]);

  useEffect(() => {
    if (!draft || addableMembers.length === 0 || selectedAddMemberIsAvailable) return;
    setAddRoleId(addableMembers[0].id);
  }, [addableMembers, draft, selectedAddMemberIsAvailable]);

  if (!draft) return null;

  const updateParticipantStance = (participantId: string, stanceId: GroupTalkStanceId) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        participants: current.participants.map((participant) =>
          participant.participantId === participantId
            ? { ...participant, stanceId: normalizeGroupTalkStanceId(stanceId) }
            : participant
        ),
      };
    });
    setError("");
  };

  const removeParticipant = (participantId: string) => {
    if (membersLocked) return;
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        participants: current.participants.filter((participant) => participant.participantId !== participantId),
      };
    });
    setError("");
  };

  const addParticipant = () => {
    if (!draft || !canAddMembers) return;
    if (!selectedAddMemberIsAvailable) {
      setError("追加できるメンバーを選んでください。");
      return;
    }
    if (isDuplicateParticipant(draft.participants, addRoleId)) {
      setError("同じ役割のメンバーは追加できません。");
      return;
    }

    const nextParticipant = createParticipant(addRoleId, addStanceId, {
      fixed: false,
      imageVariant: mode === "custom" ? 2 : 1,
    });
    setDraft({
      ...draft,
      participants: [...draft.participants, nextParticipant],
    });
    setError("");
  };

  const startSession = async () => {
    if (creating) return;
    setCreating(true);
    setError("");

    try {
      const response = await api.createGroupTalkSession(buildCreateGroupTalkSessionParams({ draft }));
      const normalizedSession = normalizeGroupTalkSessionView(response, characterName, displayMode);
      const initialUserMessage = draft.consultationText?.trim() ?? "";
      saveGroupTalkActiveSessionSnapshot({
        sessionId: normalizedSession.sessionId,
        draft,
        session: normalizedSession,
        turns: [],
        ...(initialUserMessage ? { initialUserMessage, initialUserMessageSent: false } : {}),
      });
      clearGroupTalkDraft();
      navigate(`/talk/group/sessions/${encodeURIComponent(normalizedSession.sessionId)}`, {
        replace: true,
      });
    } catch (errorValue) {
      logError(errorValue);
      setError(toUserMessage(errorValue));
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk/group" state={{ draft }} className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">AI壁打ちルーム</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="border-b border-[#EADBC8] pb-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">メンバー確認</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#5D4D46] sm:text-4xl">
            参加メンバーを確認する
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-[#6B5851]">
            相談内容に合わせたメンバーを選んでいます。必要に応じてスタンスを変更できます。
          </p>
        </section>

        <section className="mt-6 grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="rounded-[1.6rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-sm">
            <span className="inline-flex rounded-full bg-[#FFF3E9] px-3 py-1 text-[11px] font-black text-[#A46D4C]">
              {mode === "recommend" ? "おすすめ" : mode === "custom" ? "自由編成" : "テンプレート"}
            </span>
            <h2 className="mt-3 text-2xl font-black text-[#5D4D46]">{draft.title}</h2>
            <p className="mt-2 text-sm leading-7 text-[#6B5851]">{draft.description}</p>

            {draft.consultationText ? (
              <div className="mt-4 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3">
                <p className="text-xs font-black text-[#A67C52]">相談内容</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-[#4A3E39]">{draft.consultationText}</p>
              </div>
            ) : null}

            {draft.recommendationReason ? (
              <div className="mt-4 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFF7F1] px-4 py-3">
                <p className="text-xs font-black text-[#A67C52]">おすすめ理由</p>
                <p className="mt-1 text-sm leading-6 text-[#4A3E39]">{draft.recommendationReason}</p>
              </div>
            ) : null}

            {draft.recommendationConfidence === "low" ? (
              <p className="mt-3 text-sm font-bold text-[#9C867D]">
                相談内容が広いため、必要に応じてメンバーやスタンスを調整してください。
              </p>
            ) : null}
          </div>

          <div className="rounded-[1.6rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-black text-[#5D4D46]">参加メンバー</h2>
              {membersLocked ? (
                <span className="rounded-full bg-[#F5EEE7] px-3 py-1 text-[11px] font-black text-[#8F7468]">
                  メンバー固定
                </span>
              ) : null}
            </div>

            <div className="mt-4 space-y-4">
              {draft.participants.map((participant) => {
                const member = findGroupTalkMember(participant.memberId);
                const previewMember = previewMembers.find((item) => item.participantId === participant.participantId);
                const availableStances = getGroupTalkAvailableStances(
                  participant.memberId,
                  displayMode,
                  participant.availableStances
                );
                const canRemove = !membersLocked && !participant.fixed;

                return (
                  <div key={participant.participantId} className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <img
                          src={previewMember?.imageSrc ?? getGroupTalkFallbackImageSrc()}
                          alt={previewMember?.label ?? member.label}
                          className="h-14 w-14 shrink-0 rounded-full border border-[#EADBC8] bg-[#FFF7F1] object-cover"
                          onError={(event) => {
                            const target = event.currentTarget;
                            const fallbackSrc = getGroupTalkFallbackImageSrc();
                            if (target.getAttribute("src") === fallbackSrc) return;
                            target.src = fallbackSrc;
                          }}
                        />
                        <div className="min-w-0">
                          <h3 className="text-base font-black text-[#5D4D46]">{previewMember?.label ?? member.label}</h3>
                          <p className="mt-1 text-sm leading-6 text-[#6B5851]">
                            {previewMember?.roleLabel ?? participant.displayRole ?? member.roleLabel}
                          </p>
                        </div>
                      </div>
                      {canRemove ? (
                        <button
                          type="button"
                          onClick={() => removeParticipant(participant.participantId)}
                          className="rounded-full border border-[#EADBC8] bg-white px-3 py-1 text-[11px] font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
                        >
                          削除
                        </button>
                      ) : null}
                    </div>

                    {availableStances.length > 0 ? (
                      <div className="mt-4 grid gap-2">
                        {availableStances.map((stance) => {
                          const selected = participant.stanceId === stance.id;
                          return (
                            <button
                              key={stance.id}
                              type="button"
                              onClick={() => updateParticipantStance(participant.participantId, stance.id)}
                              className={`rounded-[1rem] border px-3 py-2 text-left transition ${
                                selected
                                  ? "border-[#D1907B] bg-[#FFF3EC] text-[#5D4D46]"
                                  : "border-[#EADBC8] bg-white text-[#6B5851] hover:bg-[#FEFBF9]"
                              }`}
                            >
                              <span className="block text-sm font-black">{stance.label}</span>
                              {stance.description ? (
                                <span className="mt-1 block text-xs leading-5 text-[#7B6861]">{stance.description}</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {canAddMembers ? (
              <div className="mt-5 rounded-[1.4rem] border border-dashed border-[#D9C5B9] bg-white p-4">
                <p className="text-sm font-black text-[#5D4D46]">メンバーを追加する</p>
                {addableMembers.length > 0 ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
                    <label className="text-sm font-bold text-[#6B5851]">
                      役割
                      <select
                        value={selectedAddMemberIsAvailable ? addRoleId : addableMembers[0].id}
                        onChange={(event) => setAddRoleId(event.target.value as GroupTalkMemberId)}
                        className="mt-2 w-full rounded-[1rem] border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3 text-sm"
                      >
                        {addableMembers.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={addParticipant}
                      disabled={!selectedAddMemberIsAvailable}
                      className="self-end rounded-[1rem] border border-[#D9C5B9] bg-white px-5 py-3 text-sm font-black text-[#6B5851] transition hover:bg-[#FEFBF9] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      追加
                    </button>
                  </div>
                ) : (
                  <p className="mt-3 rounded-[1rem] bg-[#F8F1EC] px-3 py-2 text-sm font-bold text-[#8F7468]">
                    追加できるメンバーはすべて参加中です。
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </section>

        {error ? (
          <p role="alert" className="mt-5 rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-4 py-3 text-sm font-bold text-[#C06D5A]">
            {error}
          </p>
        ) : null}

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Link
            to="/talk/group"
            state={{ draft }}
            className="rounded-full border border-[#EADBC8] bg-white px-5 py-3 text-center text-sm font-bold text-[#5D4D46]"
          >
            戻る
          </Link>
          <button
            type="button"
            onClick={() => void startSession()}
            disabled={creating}
            className="rounded-full bg-[#5D4D46] px-6 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creating ? "セッションを作成中..." : "このメンバーで始める"}
          </button>
        </div>
      </div>
    </main>
  );
}
